import { useState, useEffect, useCallback } from 'react';
import { Button, Input } from 'components/atoms/index';
import logoImg from '@/assets/img/logo.png';
import { useLocation, useNavigate } from 'react-router-dom';
import { publicAcademyApi, studentAuthApi } from '@/services/api';
import { getSlugPrefix, isReservedSegment, sanitizeSlug } from '@/utils/slug';
import { useSessionSlug } from '@/hooks/useSessionSlug';

// 로그인 페이지
export default function StudentLogin() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const slugPrefix = getSlugPrefix(pathname);
  const [logoUrl, setLogoUrl] = useState<string | null | undefined>(undefined);
  const [formData, setFormData] = useState({
    loginId: '',
    password: ''
  });
  const [errors, setErrors] = useState({
    loginId: '',
    password: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const { refresh: refreshSessionSlug } = useSessionSlug();

  // 이미 로그인된 경우 홈으로 리다이렉트
  useEffect(() => {
    const token = localStorage.getItem('student_token');
    if (token) {
      navigate(slugPrefix || '/', { replace: true });
    }
  }, [navigate, slugPrefix]);

  // 학원 로고 로딩 (slug 기반)
  useEffect(() => {
    setLogoUrl(undefined);
    try {
      const segments = pathname.split('/').filter(Boolean);
      const first = segments[0] || '';
      const slugCandidate = first && !isReservedSegment(first) ? sanitizeSlug(first) : '';
      if (!slugCandidate) {
        setLogoUrl(null);
        return;
      }
      publicAcademyApi
        .getByUrl(slugCandidate)
        .then((res: any) => {
        const data = res?.data || res;
        const nextLogo = data?.logoUrl || null;
          setLogoUrl(nextLogo ? String(nextLogo) : null);
        })
        .catch(() => setLogoUrl(null));
    } catch {
      setLogoUrl(null);
    }
  }, [pathname]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // 입력 시 해당 필드 에러 초기화
    setErrors(prev => ({
      ...prev,
      [name]: ''
    }));
  };

  const validateField = useCallback((name: 'loginId' | 'password', value: string) => {
    const trimmed = value.trim();
    if (!trimmed) {
      return name === 'loginId' ? '아이디를 입력해주세요.' : '비밀번호를 입력해주세요.';
    }
    return '';
  }, []);

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'loginId' || name === 'password') {
      const errorMessage = validateField(name, value);
      if (errorMessage) {
        setErrors(prev => ({
          ...prev,
          [name]: errorMessage
        }));
      }
    }
  };

  const handleFindIdClick = useCallback(() => {
    // [QA Fix] 아이디 찾기 버튼 클릭 시 전용 페이지로 즉시 이동
    const target = slugPrefix ? `${slugPrefix}/find_id` : '/find_id';
    navigate(target);
  }, [navigate, slugPrefix]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // 유효성 검사
    const newErrors = {
      loginId: validateField('loginId', formData.loginId),
      password: validateField('password', formData.password)
    };

    if (newErrors.loginId || newErrors.password) {
      setErrors(newErrors);
      return;
    }

    setIsLoading(true);
    
    try {
      const response = await studentAuthApi.login({
        loginId: formData.loginId,
        password: formData.password
      });

      if (response.success) {
        const { token, refreshToken, userId, userName, displayName, email, academyCode, academyName, gradeLabel, birthDate, academyUrl } = response.data;
        
        // 토큰 저장
        localStorage.setItem('student_token', token);
        localStorage.setItem('student_refresh_token', refreshToken);
        
        // 사용자 정보 저장
        localStorage.setItem('student_user_id', String(userId));
        localStorage.setItem('student_user_name', userName);
        localStorage.setItem('student_display_name', displayName);
        localStorage.setItem('student_email', email);
        localStorage.setItem('student_login_id', formData.loginId);
        
        if (academyCode) {
          // [QA Fix] 학원 컨텍스트 복원을 위해 코드도 보관
          localStorage.setItem('student_academy_code', academyCode);
        }
        if (academyName) localStorage.setItem('student_academy_name', academyName);
        if (gradeLabel) localStorage.setItem('student_grade_label', gradeLabel);
        if (birthDate) localStorage.setItem('student_birth_date', birthDate);
        const sanitizedResponseSlug = sanitizeSlug(academyUrl || '');
        const currentPathSlug = sanitizeSlug(slugPrefix ? slugPrefix.replace(/^\//, '') : '');
        const finalSlug = sanitizedResponseSlug || currentPathSlug;
        const sessionSlug = await refreshSessionSlug();
        const targetSlug = sessionSlug || finalSlug;
        const nextPrefix = targetSlug ? `/${targetSlug}` : slugPrefix;
        navigate(nextPrefix || '/');
      } else {
        setErrors(prev => ({
          ...prev,
          password: response.message || '로그인에 실패했습니다.'
        }));
      }
    } catch (error: any) {
      const status = error?.response?.status;
      const errorMessage = error?.response?.data?.message || '로그인 중 오류가 발생했습니다.';
      const notStudentAccount = status === 403 && typeof errorMessage === 'string' && errorMessage.includes('학생 계정');
      const noAcademyAssigned = status === 403 && typeof errorMessage === 'string' && errorMessage.includes('소속학원이 없어');

      if (noAcademyAssigned) {
        // [QA Fix] 소속 학원 미배정 계정은 모달 경고 후 진행 차단
        if (typeof window !== 'undefined') {
          window.alert(errorMessage);
        }
        setErrors({
          loginId: '',
          password: errorMessage
        });
      } else if (notStudentAccount) {
        setErrors({
          loginId: errorMessage || '학생 계정이 아닙니다.',
          password: ''
        });
      } else if (status === 404 || status === 401) {
        // [QA Fix] 아이디/비밀번호 오류 메시지 통일
        setErrors({
          loginId: '',
          password: '로그인 정보가 정확하지 않습니다'
        });
      } else {
        setErrors(prev => ({
          ...prev,
          password: errorMessage
        }));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <main className='flex justify-center flex-1 w-full bg-white lg:items-center lg:bg-transparent'>
      <section className='flex-1 h-full min-h-full px-5 py-10 lg:px-10 card'>
        <div className='flex flex-col items-center justify-center gap-4 mb-15'>
          {logoUrl === undefined ? (
            <div className='w-[120px] h-[48px]' />
          ) : (
          <img
            src={logoUrl || logoImg}
            alt="학원 로고"
            className='max-h-[48px]'
            onError={(e) => {
              const target = e.currentTarget as HTMLImageElement;
              if (target.src !== logoImg) {
                target.src = logoImg;
              }
            }}
          />
          )}
          <h1 className='text-center heading1'>학생 로그인</h1>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className='flex flex-col items-center gap-5 pb-10'>
            <div className='w-full'>
              <Input
                name="loginId"
                label="아이디"
                placeholder='아이디를 입력해주세요.'
                value={formData.loginId}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={isLoading}
              />
              {errors.loginId && <small className='error_msg'>{errors.loginId}</small>}
            </div>
            
            <div className='w-full'>
              <Input
                name="password"
                label="비밀번호"
                placeholder='비밀번호를 입력해주세요.'
                type='password'
                value={formData.password}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={isLoading}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSubmit(e as any);
                  }
                }}
              />
              {errors.password && <small className='error_msg'>{errors.password}</small>}
            </div>
          </div>
          
          <Button 
            type='submit' 
            className='w-full mb-3' 
            disabled={isLoading}
          >
            {isLoading ? '로그인 중...' : '로그인'}
          </Button>
        </form>
        
        <div className='flex items-center justify-center gap-1'>
          <Button size='small' text intent='secondary' type='button' onClick={handleFindIdClick}>
            아이디 찾기
          </Button>
        </div>
      </section>
    </main>
  );
}